// src/pages/try-editor/claim-try-diagrams.ts
import { saveBringingIn } from '@/diagram-persistence/bring-in';
import type { DiagramStore } from '@/diagram-persistence/diagram-store';

/** Outcome of a `claimTryDiagrams` run. `claimed` counts documents newly
 *  copied into the account store this run (a draft the plan had no free
 *  active slot for lands archived and counts as claimed: it is in the
 *  account, and nothing was lost); `failed` counts documents this
 *  run could not resolve one way or the other (unreadable source, ambiguous
 *  destination check, or a rejected save) and therefore left untouched in
 *  the try store for a later retry. A document the account already held an
 *  equal-or-newer copy of (see the re-claim guard below) counts as neither —
 *  it was cleared from the try store, but nothing new was claimed. */
export type ClaimTryDiagramsResult = { claimed: number; failed: number };

/**
 * Copies every document out of the /try store and into the signed-in
 * account store, then removes each copied document from the try store. This
 * is what makes good on the sign-up wall's promise ("everything you made
 * here will be waiting in your account") now that /try work lives in its own
 * database (`azabu-try-diagrams`) instead of implicitly becoming account
 * data — see `src/pages/try-editor/try-store.ts`.
 *
 * Deliberately NOT the old implicit "unowned local cache" claim path
 * (`src/doc-sync/ownership-prepare.ts`): that path could trigger
 * `wipe-and-claim` and destroy anonymous work when the browser carried a
 * stamp from a different account. Copying out of a wholly separate database
 * avoids that failure mode entirely — nothing here ever wipes anything.
 *
 * Semantics:
 * - Copy-then-clear, per document: a try document is deleted only after its
 *   copy into the account store has succeeded. Existing ids are kept (try
 *   ids are fresh uuids, so there's no collision risk against the account
 *   store).
 * - Re-claim guard: before copying, the account store is checked for an
 *   existing copy of that id. If the account already holds one that is not
 *   older than the try copy (`updatedAt` comparison), the save is skipped —
 *   copying anyway would silently overwrite newer account-side edits with
 *   stale try content. This is exactly what happens after a PARTIAL
 *   success: `SyncedDocStore.save` does `local.save(doc)` and THEN
 *   `remote.put(...)`; if signing in offline/flaky makes `remote.put`
 *   reject, the whole `save()` rejects and the document correctly stays in
 *   the try store — but it is already sitting in the account's local store,
 *   visible and editable. A later re-run of this function must not treat
 *   that stale try copy as ground truth. The try-store copy is still
 *   cleared in this case (there's nothing left to retry), it just isn't
 *   counted as newly `claimed`. A failure to read the account copy (rather
 *   than a definitive answer) is treated as ambiguous and never destructive:
 *   the document is left untouched in the try store for a later attempt —
 *   see `failed` below.
 * - Idempotent: once a document is copied and cleared it's gone from the try
 *   store, so re-running (e.g. a second sign-in, or React StrictMode's
 *   double effect invocation) finds nothing left to claim.
 * - Never refused for want of room: this is "bringing work in", so a draft
 *   the account has no free active slot for is saved archived instead
 *   (`saveBringingIn`: a 402 retries with `archive: true`; a 423 on the create
 *   means the id is already in the account archived). Either way the try copy
 *   is cleared once the document has landed.
 * - Failure-safe: a save that fails (offline, quota, conflict) leaves that
 *   document in the try store so the next sign-in retries it. One
 *   document's failure never aborts the others, and never throws out of
 *   this function.
 * - Thumbnails are carried when the try store's `list()` already surfaced
 *   one for that document (cheap — no extra store round-trip). A thumbnail
 *   copy failure does not block clearing the document: a lost thumbnail
 *   just regenerates on next open, but a lost document does not.
 *
 * Returns `{ claimed, failed }` — see `ClaimTryDiagramsResult`. Callers use
 * `claimed` to decide whether to show a toast (silent when 0) and `failed`
 * to avoid claiming full success when some documents didn't make it.
 */
export async function claimTryDiagrams(
    tryStore: DiagramStore | null,
    accountStore: DiagramStore | null,
): Promise<ClaimTryDiagramsResult> {
    if (tryStore == null || accountStore == null) return { claimed: 0, failed: 0 };

    let metas;
    try {
        metas = await tryStore.list();
    } catch {
        // Try store unreadable right now — nothing we can claim. A later
        // sign-in (or app reload) gets another chance.
        return { claimed: 0, failed: 0 };
    }

    let claimed = 0;
    let failed = 0;
    for (const meta of metas) {
        let doc;
        try {
            doc = await tryStore.load(meta.id);
        } catch {
            failed += 1;
            continue; // unreadable — leave it for next time, try the rest
        }
        if (doc == null) continue; // vanished between list() and load()

        let accountCopy;
        try {
            accountCopy = await accountStore.load(meta.id);
        } catch {
            // Can't tell whether the account already holds an equal-or-newer
            // copy — a destructive guess here is exactly the silent-overwrite
            // bug this guard exists to prevent. Leave the try-store copy
            // untouched and retry next sign-in.
            failed += 1;
            continue;
        }

        if (accountCopy != null && accountCopy.updatedAt >= doc.updatedAt) {
            // Already claimed in an earlier run (or edited in the account
            // since) — copying now would silently stomp the account's
            // current revision. Nothing new to claim; just clear the stale
            // try-store copy.
            try {
                await tryStore.delete(meta.id);
            } catch {
                // Non-fatal: leave it for a later pass, which repeats this
                // same skip (never destructive either way).
            }
            continue;
        }

        try {
            await saveBringingIn(accountStore, doc);
        } catch {
            // Save failed — leave it in the try store so the next sign-in
            // retries it. Never delete a document whose copy didn't land.
            failed += 1;
            continue;
        }

        if (meta.thumbnail != null) {
            try {
                await accountStore.saveThumbnail(meta.id, meta.thumbnail);
            } catch {
                // Non-fatal: the document itself is already safely copied,
                // and a thumbnail regenerates on next open.
            }
        }

        try {
            await tryStore.delete(meta.id);
        } catch {
            // The copy already succeeded, so the document is safe in the
            // account store even if we can't clear the try-store side right
            // now — worst case a later run re-saves the same id (a no-op
            // upsert), not a duplicate.
        }
        claimed += 1;
    }
    return { claimed, failed };
}
